import { Injectable } from '@angular/core';
import { HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { AUTH_REQUIRED } from '../auth.config';
import { AuthState } from '../state/auth.state';
import { AuthActions } from '../state/auth.actions';

@Injectable({ providedIn: 'root' })
export class MicroserviceAuthInterceptor implements HttpInterceptor {
  private refresh$?: Observable<boolean>;

  constructor(private store: Store) {}

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    const needsAuth = AUTH_REQUIRED.some((p) => req.url.includes(p));
    if (!needsAuth) return next.handle(req);

    // wait until token is valid, then attach header and forward request
    return this.ensureTokenOnce().pipe(switchMap(() => next.handle(this.attach(req))));
  }

  private ensureTokenOnce(): Observable<boolean> {
    // if valid, short-circuit
    if (this.store.selectSnapshot(AuthState.isTokenValid)) return of(true);

    // reuse in-flight refresh
    if (this.refresh$) return this.refresh$;

    this.refresh$ = this.store.dispatch(new AuthActions.EnsureToken()).pipe(
      map(() => this.store.selectSnapshot(AuthState.isTokenValid)),
      finalize(() => (this.refresh$ = undefined)),
      shareReplay(1)
    );
    return this.refresh$;
  }

  private attach(r: HttpRequest<any>): HttpRequest<any> {
    if (r.headers.has('Authorization')) return r;
    const header = this.store.selectSnapshot(AuthState.header);
    return header ? r.clone({ setHeaders: { Authorization: header } }) : r;
  }
}
